import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Megaphone, EyeOff } from "lucide-react";

/**
 * Bouton "Diffuser" avec menu déroulant pour multi-jours.
 *
 * Props:
 *  - dates: string[] — toutes les dates uniques de l'événement (ISO YYYY-MM-DD)
 *  - publishedDates: string[] — dates déjà diffusées
 *  - onPublish(date|null) — null = tout l'événement
 *  - onUnpublish(date|null)
 *  - variant: "primary" | "secondary"
 *  - mode: "publish" | "unpublish"
 */
export default function DatePublishMenu({
  dates,
  publishedDates,
  onPublish,
  onUnpublish,
  variant = "primary",
  mode = "publish",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const handler = mode === "publish" ? onPublish : onUnpublish;
  const sortedDates = [...(dates || [])].sort();
  const isMultiDay = sortedDates.length > 1;

  // Si pas multi-jours, on ne montre PAS de menu : un seul clic = action sur l'événement complet
  if (!isMultiDay) {
    return (
      <button
        type="button"
        onClick={() => handler(null)}
        className={`btn-${variant}`}
        data-testid={testid}
      >
        {mode === "publish" ? <Megaphone size={14} strokeWidth={2.5} /> : <EyeOff size={14} strokeWidth={2.5} />}
        {mode === "publish" ? "Assignations terminées" : "Masquer"}
      </button>
    );
  }

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`btn-${variant}`}
        data-testid={testid}
        aria-expanded={open}
      >
        {mode === "publish" ? <Megaphone size={14} strokeWidth={2.5} /> : <EyeOff size={14} strokeWidth={2.5} />}
        {mode === "publish" ? "Diffuser…" : "Masquer…"}
        <ChevronDown size={14} strokeWidth={2.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <span
          role="menu"
          className="absolute z-50 top-full right-0 mt-2 rounded-xl shadow-xl min-w-[280px] py-1"
          style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
          data-testid={`${testid}-menu`}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              handler(null);
            }}
            className="w-full text-left px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[var(--surface-3)] transition-colors"
            data-testid={`${testid}-all`}
          >
            <Megaphone size={14} strokeWidth={2.5} />
            <span className="flex-1">
              {mode === "publish" ? "Tout l'événement" : "Masquer tout"}
              <span className="block text-[10px] font-normal uppercase tracking-widest text-[var(--muted)] mt-0.5">
                {sortedDates.length} journées
              </span>
            </span>
          </button>
          <div className="border-t my-1" style={{ borderColor: "var(--border)" }} />
          <div className="px-4 py-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--muted)]">
            Par journée
          </div>
          {sortedDates.map((d) => {
            const isPublished = (publishedDates || []).includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setOpen(false);
                  handler(d);
                }}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--surface-3)] transition-colors"
                data-testid={`${testid}-date-${d}`}
              >
                {isPublished ? (
                  <Check size={12} strokeWidth={3} className="text-[var(--text)]" />
                ) : (
                  <span className="w-3" />
                )}
                <span className="flex-1 tabular">{d}</span>
                {isPublished && (
                  <span className="text-[10px] uppercase tracking-widest text-[var(--muted)]">Diffusée</span>
                )}
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
}
